import {
  QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { UserPhoneNumbersQueryKey } from '../../queryKey';
import { fetchCreatePhoneNumber } from './service';
import { UseCreatePhoneNumberProps } from './props';
import { UserPhoneNumber } from '@starsoft/common/models';
import { useAxiosInstance } from '@starsoft/common/hooks';
import { AxiosInstance } from 'axios';
import { CreatePhoneNumberInputDto } from '@starsoft/common/dto';

/**
 * Custom hook to create a new phone number for the user
 *
 * This hook handles creating a new phone number entry, managing the mutation state,
 * and handling side effects like cache invalidation after successful creation.
 * It uses React Query's mutation capabilities for state management.
 *
 * @param {UseCreatePhoneNumberProps} props - Optional configuration object
 * @param {Function} props.onSuccess - Optional callback function to execute on successful creation
 *
 * @returns {Object} An object containing:
 *   - createPhoneNumber: Function to trigger the creation mutation
 *     @param {CreatePhoneNumberInputDto} data - The phone number data to create
 *     @returns {Promise<UserPhoneNumber>} The created phone number entry
 *   - isPending: Boolean indicating if mutation is in progress
 */
export function useCreatePhoneNumber(props?: UseCreatePhoneNumberProps) {
  const { onSuccess: customOnSuccess } = props ?? {};
  const queryClient: QueryClient = useQueryClient();
  const axiosInstance: AxiosInstance = useAxiosInstance();

  /**
   * Handles side effects after successful phone number creation
   * - Invalidates phone numbers query to trigger refetch
   * - Executes custom success callback if provided
   *
   * @param {UserPhoneNumber} userPhoneNumber - The newly created phone number entry
   */
  function onSuccess(userPhoneNumber: UserPhoneNumber) {
    queryClient.invalidateQueries({
      queryKey: [UserPhoneNumbersQueryKey.Me],
    });
    if (customOnSuccess) {
      customOnSuccess(userPhoneNumber);
    }
  }

  const { mutate, isPending } = useMutation({
    mutationFn: (data: CreatePhoneNumberInputDto) =>
      fetchCreatePhoneNumber(data, axiosInstance),
    onSuccess,
  });

  return {
    createPhoneNumber: mutate,
    isPending,
  };
}
